import { useContext, useEffect, useState } from "react";
import styles from "../BundleBuilder.module.css";
import {
    BlockStack,
    Box,
    Button,
    Card,
    Collapsible,
    Divider,
    DropZone,
    FormLayout,
    Icon,
    Label,
    Tooltip,
} from "@shopify/polaris";
import { BundleTypes } from "../../constants";
import { MdOutlineArrowBackIosNew } from "react-icons/md";
import BundleInfo from "./BundleInfo";
import Discount from "./Discount";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { DeleteIcon, DiscountIcon, InfoIcon, PlusCircleIcon } from "@shopify/polaris-icons";
import { StepsType } from "../../Types";
import { postData } from "../../../../utilities/networkCalls";
import config from "../../../../config";
import TextFields from "./TextFields";
import useBundleBuilderMixpanelHook from "../hooks/useBundleBuilderMixpanelHook";
import { truncateFileName } from "../../../../utilities/functions";
import BBStep from "./BBStep";
import { DEFAULT_STEP } from "../hooks/useBundleBuilderHook";
import RHFSwatchesSelectorWrapper from "../../../../components/RHFSwatchesSelectorWrapper/RHFSwatchesSelectorWrapper";
import { AppConfigContext } from "../../../../context/AppConfigContext";
import { getConditionMessage } from "../../../../utilities/constants";
import ReviewAddOnProducts from "./ReviewAddOnProdcts";
import MantleDoc, { mantleDocHandles } from "../../../../components/MantleDoc";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { BundleBuilderContext, BundleBuilderContextType } from "../BundleBuilder";
import { FormRadioButton, FormTextField } from "../../../../components/FormFields";

const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1MB
const MAX_FILE_COUNT = 1; // Max allowed number of files
const ACCEPTED_FILE_TYPES = ["image/jpeg", "image/png", "image/gif"];

const BundleBuilderContentForm = () => {
    const { control, setValue, getValues, trigger } = useFormContext();
    const { fields: stepFields, replace: replaceSteps } = useFieldArray({ control, name: "steps" });
    const steps = useWatch({ control, name: "steps" }) as StepsType[];
    const reviewStep = useWatch({ control, name: "review_step" });

    const { handleNext, isMobile, SwatchesBundleConfigTouchedRef } = useContext(
        BundleBuilderContext,
    ) as BundleBuilderContextType;
    const { trackBbStep } = useBundleBuilderMixpanelHook();
    const { money_format } = useContext(AppConfigContext);
    const [expandReviewStep, setExpandReviewStep] = useState(false);
    const [expandConfigureSteps, setExpandConfigureSteps] = useState<boolean>(true);
    const [expandStep, setExpandStep] = useState(0);
    const [addedNow, setAddedNow] = useState(false);

    const handleAddStep = () => {
        const totalSteps = steps?.length || 0;
        const updatedArray = steps?.length
            ? [...steps, { ...DEFAULT_STEP, step_label: `Step ${totalSteps + 1}` }]
            : [{ ...DEFAULT_STEP, step_label: `Step ${totalSteps + 1}` }];
        replaceSteps(updatedArray);
        trackBbStep({ cta: "Add Step", stepId: totalSteps + 1, ctaType: "Add Step" });
        setExpandStep(totalSteps);
        setAddedNow(true);
        setTimeout(() => {
            setAddedNow(false);
            const el = document.getElementById(`step-${totalSteps - 1}`);
            if (el) {
                const offset = 14;
                const top = el.getBoundingClientRect().top + window.scrollY - offset;
                window.scrollTo({
                    top,
                    behavior: "smooth",
                });
            }
        }, 400);
    };

    const handleDragEnd = (result: any) => {
        if (!result.destination) return;
        const reorderedSteps = Array.from(steps);
        const [movedStep] = reorderedSteps.splice(result.source.index, 1);
        reorderedSteps.splice(result.destination.index, 0, movedStep);
        setValue("steps", [...reorderedSteps], { shouldDirty: true });
        trackBbStep({ cta: "Steps Reorder", stepId: result.source.index + 1, ctaType: "Add Step" });
        setExpandStep(result.destination.index);
    };

    const handleEditStep = (idx: number, key: any, value?: any) => {
        let updatedSteps: StepsType[] = [...steps];
        // delete/copy change the array's length/membership, so the field array
        // must be replaced (regenerates field ids). Every other branch below
        // only edits values in place — use setValue so field ids (and therefore
        // BBStep/input identities) stay stable and typing doesn't lose focus.
        const isStructuralChange = key == "delete" || key === "copy";
        if (key == "delete") {
            updatedSteps = updatedSteps.filter((_, index) => index != idx);
        } else if (key === "copy") {
            const originalStep = updatedSteps[idx];
            const copiedStep: StepsType = { ...originalStep, step_label: originalStep.step_label + ": Copy" };
            updatedSteps.splice(idx + 1, 0, copiedStep);
            setExpandStep(idx + 1);
        } else if (key == "enable_subSteps") {
            updatedSteps[idx] = {
                ...updatedSteps[idx],
                subSteps: [],
                enable_subSteps: value,
                ...(value === true && { allow_product_custom_text: false }),
            };
        } else if (typeof key === "object" && key !== null) {
            updatedSteps[idx] = { ...updatedSteps[idx], ...key };
        } else {
            const updatedStep: StepsType = { ...updatedSteps[idx] };
            if (key == "step_content_type") {
                if (value == "fields") {
                    updatedStep["fields"] = [
                        { field_type: "input", field_placeholder: "Message", field_title: "Field", is_required: false },
                    ];
                }
                if (value == "free_gift") {
                    updatedStep.render_as_variants = true;
                    updatedStep.show_step_conditions = true;
                    updatedStep.step_conditions = [
                        {
                            basis: "items_count",
                            limit: 1,
                            type: "exact_products",
                            msg: getConditionMessage("items_count", "exact_products"),
                        },
                    ];
                } else {
                    updatedStep.render_as_variants = false;
                    updatedStep.show_step_conditions = false;
                    updatedStep.step_conditions = [
                        {
                            basis: "items_count",
                            limit: 10,
                            type: "max_products",
                            msg: getConditionMessage("items_count", "max_products"),
                        },
                    ];
                }
            }
            if (key == "product_selection") {
                updatedStep.products = [];
                updatedStep.collections = [];
            }
            trackBbStep({ cta: "Edit Step", stepId: idx + 1, ctaType: key });
            (updatedStep as any)[key] = value;
            updatedSteps[idx] = updatedStep;
        }
        if (isStructuralChange) {
            replaceSteps(updatedSteps);
        } else {
            // Update only the changed item, not the whole array — useFieldArray
            // keeps its own field/id bookkeeping for the "steps" path, and
            // overwriting the root with setValue can desync it. Writing to the
            // single `steps.${idx}` leaf avoids touching that bookkeeping and
            // any field identities, so typing doesn't remount inputs.
            setValue(`steps.${idx}`, updatedSteps[idx], { shouldDirty: true });
        }
    };

    const handleEditReviewStep = <K extends keyof any>(key: K, value?: any) => {
        setValue(`review_step.${key as string}` as any, value, { shouldDirty: true });
        trackBbStep({ cta: "Review Step", ctaType: String(key) });
    };

    const handleAddFields = (pIdx: number, action: "add" | "remove", idx?: number) => {
        let updatedFields = [...steps[pIdx].fields];
        if (action == "add") {
            updatedFields.push({
                field_type: "input",
                field_placeholder: "Message",
                field_title: `Field ${updatedFields.length + 1}`,
                is_required: false,
            });
        } else if (action == "remove" && idx != undefined) {
            updatedFields = updatedFields.filter((_, id) => idx != id);
        }
        trackBbStep({ cta: "Add Fields", stepId: (steps[pIdx]?.fields?.length ?? 0) + 1, ctaType: "Add Field" });
        handleEditStep(pIdx, "fields", updatedFields);
    };

    const handleEditFields = (
        pIdx: number,
        idx: number,
        subKey:
            | "field_type"
            | "field_title"
            | "field_placeholder"
            | "dropdown_options"
            | "default_value"
            | "is_required"
            | "file_upload_settings",
        value: any,
    ) => {
        const updatedFields = steps[pIdx].fields.map((field: any, fIdx: number) => {
            if (fIdx === idx) {
                const updatedField = { ...field, [subKey]: value };
                if (subKey === "field_type" && value === "file_upload") {
                    if (!updatedField.file_upload_settings) {
                        updatedField.file_upload_settings = {
                            max_file_size: 5,
                            allow_multiple: false,
                            allowed_file_types: ["images"],
                        };
                    }
                }
                return updatedField;
            }
            return field;
        });
        trackBbStep({ cta: "Edit Fields", stepId: pIdx + 1, ctaType: subKey });
        handleEditStep(pIdx, "fields", updatedFields);
    };

    const handleDropZoneDrop = async (
        idx: number,
        _dropFiles: File[],
        newFiles: File[],
        step: "steps" | "free_gift" | "review_step" = "steps",
    ) => {
        const remainingFileCount = 1;
        if (newFiles.length > remainingFileCount) {
            shopify.toast.show(`You can only upload up to ${MAX_FILE_COUNT} files.`, { isError: true });
            return [];
        }

        const validFiles: File[] = [];
        for (const file of newFiles) {
            if (file?.name) {
                const fileName = truncateFileName(file.name, 20);
                if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
                    shopify.toast.show(`${fileName} is not a supported file type.`, { isError: true });
                    await new Promise((resolve) => setTimeout(resolve, 1000));
                } else if (file.size > MAX_FILE_SIZE) {
                    shopify.toast.show(`${fileName} exceeds the 5MB file size limit.`, { isError: true });
                    await new Promise((resolve) => setTimeout(resolve, 1000));
                } else {
                    validFiles.push(file);
                }
            }
        }

        const filesToProcess = validFiles.slice(0, remainingFileCount);

        for (const file of filesToProcess) {
            const placeholderItem = {
                originalSrc: "",
                alt: file.name || "icon",
                mediaContentType: "IMAGE",
            } as StepsType["step_icon"];
            if (step === "review_step") {
                setValue("review_step.step_icon" as any, placeholderItem, { shouldDirty: true });
            } else {
                handleEditStep(idx, "step_icon", placeholderItem);
            }

            const data = new FormData();
            data.append("file", file);

            try {
                const response = await postData({ url: `/${config.appName}/upload`, payload: data });
                if (!response || !response.data || !response.data.url) {
                    throw new Error("Invalid response format");
                }

                if (step === "review_step") {
                    setValue(
                        "review_step.step_icon" as any,
                        { ...getValues("review_step.step_icon"), originalSrc: response.data.url },
                        { shouldDirty: true },
                    );
                } else {
                    handleEditStep(idx, "step_icon", {
                        ...getValues(`steps.${idx}.step_icon` as any),
                        originalSrc: response.data.url,
                    });
                }
            } catch (error) {
                console.error("Error during file upload:", error);
                if (step === "review_step") {
                    setValue("review_step.step_icon" as any, null, { shouldDirty: true });
                } else {
                    handleEditStep(idx, "step_icon", null);
                }
                shopify.toast.show("Upload failed! Please try again.", { isError: true });
            }
        }
    };

    useEffect(() => {
        const allowPreSelectedProducts = getValues("allow_pre_selected_products");
        const preSelectedProducts = getValues("pre_selected_products");
        if (!preSelectedProducts?.length || !allowPreSelectedProducts) return;

        const stepProducts = (steps || []).flatMap((s: StepsType) =>
            s?.enable_subSteps ? s.subSteps.flatMap((ss) => ss.products.map((p) => p.id)) : s.products.map((p) => p.id),
        );
        const stepCollections = (steps || []).flatMap((s: StepsType) =>
            s?.enable_subSteps
                ? s.subSteps.flatMap((ss) => ss.collections.map((c) => c.id))
                : s.collections.map((c) => c.id),
        );

        const newPreSelectedProductsList = preSelectedProducts.filter((product: any) => {
            const isDirectProduct = stepProducts.includes(product.id);
            const isInAllowedCollection = product?.collections?.some((c: any) => stepCollections.includes(c));
            return isDirectProduct || isInAllowedCollection;
        });

        if (newPreSelectedProductsList.length !== preSelectedProducts?.length) {
            setValue("pre_selected_products", newPreSelectedProductsList, { shouldDirty: true });
            shopify.toast.show("Only products included in the bundle can be set as preselected", {
                isError: true,
                duration: 1500,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [steps]);

    return (
        <div className={styles.formWrapper}>
            <div style={{ marginBottom: "4px" }}>
                <Divider borderColor="border" />
            </div>
            <div className={styles.collapsibleFormBox}>
                <BundleInfo />

                <div className={styles.dividerStyle}>
                    <Divider borderColor="border" />
                </div>

                <div
                    className={styles.sectionTitleWrapper}
                    style={{ cursor: "pointer" }}
                    onClick={() => setExpandConfigureSteps(!expandConfigureSteps)}
                >
                    <div>
                        <MantleDoc
                            handle={mantleDocHandles[BundleTypes.BUNDLE_BUILDER].configureSteps}
                            title="Configure Steps"
                        />
                    </div>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            transform: expandConfigureSteps ? "rotate(90deg)" : "rotate(270deg)",
                        }}
                    >
                        <MdOutlineArrowBackIosNew size={14} cursor="pointer" />
                    </div>
                </div>

                <Collapsible
                    open={expandConfigureSteps}
                    transition={{ duration: "500ms", timingFunction: "ease-in-out" }}
                    expandOnPrint
                    id="configure-steps-collapsible"
                >
                    <div className={styles.stepTabContainer}>
                        {
                            // @ts-expect-error required
                            <DragDropContext onDragEnd={handleDragEnd}>
                                {
                                    // @ts-expect-error required
                                    <Droppable droppableId="droppable-tabs">
                                        {(provided) => (
                                            <div
                                                {...provided.droppableProps}
                                                ref={provided.innerRef}
                                                className={styles.stepsDroppableContainer}
                                            >
                                                {stepFields.map((field, index) => (
                                                    // @ts-expect-error required
                                                    <Draggable key={field.id} draggableId={field.id} index={index}>
                                                        {(provided, snapshot) => (
                                                            <div
                                                                id={`step-${index}`}
                                                                ref={provided.innerRef}
                                                                {...provided.draggableProps}
                                                                className={`${styles.stepDraggableItem}`}
                                                            >
                                                                <BBStep
                                                                    provided={provided}
                                                                    key={"step" + index}
                                                                    step={steps[index]}
                                                                    index={index}
                                                                    snapshot={snapshot}
                                                                    handleEditFields={handleEditFields}
                                                                    handleAddFields={handleAddFields}
                                                                    handleDropZoneDrop={(
                                                                        _dropFiles: any,
                                                                        acceptedFiles: any,
                                                                    ) =>
                                                                        handleDropZoneDrop(
                                                                            index,
                                                                            _dropFiles,
                                                                            acceptedFiles,
                                                                        )
                                                                    }
                                                                    isMobile={isMobile}
                                                                    handleEditStep={handleEditStep}
                                                                    submit={() => trigger("steps")}
                                                                    expandStep={expandStep}
                                                                    setExpandStep={setExpandStep}
                                                                    addedNow={addedNow}
                                                                    money_format={money_format}
                                                                />
                                                            </div>
                                                        )}
                                                    </Draggable>
                                                ))}
                                                {provided.placeholder as any}
                                            </div>
                                        )}
                                    </Droppable>
                                }
                            </DragDropContext>
                        }
                        <div className={styles.customAddStepBtn}>
                            <Button onClick={handleAddStep} variant="secondary" fullWidth icon={PlusCircleIcon}>
                                Add Step
                            </Button>
                        </div>

                        {reviewStep?.is_enabled ? (
                            <Card padding="200" background="bg-surface">
                                <div
                                    onClick={() => setExpandReviewStep((ps) => !ps)}
                                    className={styles.customCardLabelWrapper}
                                    style={{
                                        marginBottom: "5px",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "flex-start",
                                            alignItems: "center",
                                            gap: 5,
                                        }}
                                    >
                                        <Icon source={DiscountIcon} />
                                        <Label id="review_step">{`Review Step`}</Label>
                                        <div
                                            style={{
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                transform: expandReviewStep ? "rotate(90deg)" : "rotate(270deg)",
                                            }}
                                        >
                                            <MdOutlineArrowBackIosNew size={14} cursor="pointer" />
                                        </div>
                                    </div>
                                    <div className={styles.stepDragIconWrapper}>
                                        <span
                                            className={styles.stepDragIcon}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleEditReviewStep("is_enabled", false);
                                                trackBbStep({
                                                    cta: "Delete Step",
                                                    stepId: (steps.length || 0) + 1,
                                                    ctaType: "Delete Step Review",
                                                });
                                            }}
                                        >
                                            <Icon source={DeleteIcon} tone="critical" />
                                        </span>
                                    </div>
                                </div>
                                <Collapsible
                                    open={expandReviewStep}
                                    transition={{ duration: "500ms", timingFunction: "ease-in-out" }}
                                    expandOnPrint
                                    id="basic-collapsible"
                                >
                                    <FormLayout>
                                        <Box paddingInlineStart="400">
                                            <BlockStack gap="400">
                                                <div
                                                    className={`${styles.formRow}`}
                                                    style={{ display: "flex", flexDirection: "column" }}
                                                >
                                                    <FormTextField
                                                        name="review_step.step_label"
                                                        label="Review Label"
                                                        placeholder="Label"
                                                        value={reviewStep?.step_label}
                                                        onChange={(val) => handleEditReviewStep("step_label", val)}
                                                        maxLength={80}
                                                        showCharacterCount
                                                    />
                                                    <FormTextField
                                                        name="review_step.page_title"
                                                        label="Page Title (optional)"
                                                        placeholder="Page Title"
                                                        value={reviewStep?.page_title}
                                                        onChange={(val) => handleEditReviewStep("page_title", val)}
                                                        maxLength={80}
                                                        showCharacterCount
                                                    />
                                                </div>
                                                <div>
                                                    <div
                                                        style={{ marginTop: 4, display: "flex", alignItems: "center" }}
                                                    >
                                                        <div style={{ width: "100%" }}>
                                                            <span>
                                                                <Label id="review_widget_icon">
                                                                    <span
                                                                        style={{
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            gap: 5,
                                                                        }}
                                                                    >
                                                                        Add Widget Icon
                                                                        <Tooltip
                                                                            width="wide"
                                                                            content="Upload image: Accepts .jpg, .png, .svg and .gif files up to 5MB. Image dimensions should not be greater than 512x512 pixels."
                                                                        >
                                                                            <Icon source={InfoIcon} />
                                                                        </Tooltip>
                                                                    </span>
                                                                </Label>
                                                            </span>
                                                            <div
                                                                style={{
                                                                    marginTop: 4,
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    width: "100%",
                                                                }}
                                                            >
                                                                {reviewStep?.step_icon == null ? (
                                                                    <div style={{ width: 40, height: 40 }}>
                                                                        <DropZone
                                                                            onDrop={(files) =>
                                                                                handleDropZoneDrop(
                                                                                    0,
                                                                                    [],
                                                                                    files,
                                                                                    "review_step",
                                                                                )
                                                                            }
                                                                        >
                                                                            <DropZone.FileUpload
                                                                                actionTitle="Click to upload or drag and drop"
                                                                                actionHint=""
                                                                            />
                                                                        </DropZone>
                                                                    </div>
                                                                ) : (
                                                                    <div
                                                                        className={styles.uploadedImage}
                                                                        style={{
                                                                            height: "50px",
                                                                            minWidth: reviewStep.step_icon.originalSrc
                                                                                ? "50px"
                                                                                : "100%",
                                                                            width: "min-content",
                                                                        }}
                                                                    >
                                                                        {reviewStep.step_icon.originalSrc ? (
                                                                            <img
                                                                                src={reviewStep.step_icon.originalSrc}
                                                                                alt={reviewStep.step_icon.alt}
                                                                                style={{
                                                                                    objectFit: "contain",
                                                                                    width: "auto",
                                                                                    height: "100%",
                                                                                }}
                                                                            />
                                                                        ) : (
                                                                            <div
                                                                                className={styles.uploadingPlaceholder}
                                                                                style={{ width: "100%" }}
                                                                            >
                                                                                Uploading...
                                                                            </div>
                                                                        )}

                                                                        <button
                                                                            className={styles.deleteButton}
                                                                            onClick={() =>
                                                                                handleEditReviewStep("step_icon", null)
                                                                            }
                                                                        >
                                                                            <Icon source={DeleteIcon} />
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className={styles.label}>Secondary Button Type</label>
                                                    <div className={styles.formRow} style={{ marginTop: "5px" }}>
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                flexDirection: "column",
                                                                justifyContent: "space-between",
                                                            }}
                                                        >
                                                            <div
                                                                style={{
                                                                    display: "flex",
                                                                    justifyContent: "start",
                                                                    alignItems: "center",
                                                                    gap: "5px",
                                                                }}
                                                            >
                                                                <FormRadioButton
                                                                    label="Edit Bundle"
                                                                    id="edit_bundle"
                                                                    name="secondary_button_type"
                                                                    value="edit_bundle"
                                                                />
                                                                <Tooltip
                                                                    width="wide"
                                                                    content={`When customers click Edit Bundle, they will be taken back to the previous step of the bundle builder.
                                                        They can adjust their selections (like changing products or quantities) without losing all progress.`}
                                                                >
                                                                    <Icon source={InfoIcon} />
                                                                </Tooltip>
                                                            </div>
                                                            <div
                                                                style={{
                                                                    display: "flex",
                                                                    justifyContent: "start",
                                                                    alignItems: "center",
                                                                    gap: "5px",
                                                                }}
                                                            >
                                                                <FormRadioButton
                                                                    label="Start Again"
                                                                    id="start_again"
                                                                    name="secondary_button_type"
                                                                    value="start_again"
                                                                />
                                                                <Tooltip
                                                                    width="wide"
                                                                    content={`When customers click Start Again, the bundle builder will reset completely.
                                                        All current selections will be cleared, and the customer will be taken back to Step 1 to rebuild their bundle from scratch.`}
                                                                >
                                                                    <Icon source={InfoIcon} />
                                                                </Tooltip>
                                                            </div>
                                                        </div>
                                                        <span></span>
                                                    </div>
                                                </div>
                                            </BlockStack>
                                        </Box>

                                        <Divider borderColor="border" />

                                        <ReviewAddOnProducts money_format={money_format} />
                                    </FormLayout>
                                </Collapsible>
                            </Card>
                        ) : (
                            <Button
                                onClick={() => handleEditReviewStep("is_enabled", true)}
                                variant="secondary"
                                fullWidth
                            >
                                Add Review Step
                            </Button>
                        )}
                    </div>
                </Collapsible>
                <div className={styles.dividerStyle}>
                    <Divider borderColor="border" />
                </div>

                <Discount />

                <div className={styles.dividerStyle}>
                    <Divider borderColor="border" />
                </div>

                <RHFSwatchesSelectorWrapper
                    bundleType={BundleTypes.BUNDLE_BUILDER}
                    SwatchesBundleConfigTouchedRef={SwatchesBundleConfigTouchedRef}
                    productSource={{
                        type: "specific",
                        value: (steps || []).flatMap((s) =>
                            s?.enable_subSteps ? s.subSteps.flatMap((ss) => ss.products) : s.products,
                        ),
                    }}
                />

                <div className={styles.dividerStyle}>
                    <Divider borderColor="border" />
                </div>

                <TextFields />
            </div>
            <div className={styles.dividerStyle}>
                <Divider borderColor="border" />
            </div>

            <div className={styles.bottomRowCta}>
                <span></span>
                <Button variant="secondary" onClick={() => handleNext(1, "Design")}>
                    Next
                </Button>
            </div>
        </div>
    );
};

export default BundleBuilderContentForm;
